'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';

import {
    buildAttributeFilterParams,
    countActiveAttributeFilters,
    createEmptyAttributeFiltersSelection,
    hasInvalidAttributeRanges,
    parseAttributeFilterParams,
    pruneAttributeFiltersSelection,
    setAttributeRangeSelection,
    setAttributeValuesSelection
} from '@/utilities/attributeFilters';
import { ATTRIBUTE_FILTER_QUERY_KEY, BRAND_FILTER_QUERY_KEY, BRAND_SEARCH_DEBOUNCE_MS } from '@/utilities/attributeFilters/constants';
import { AttributeFiltersSelection, AttributeValueSelection } from '@/utilities/attributeFilters/models';

import { UseCategoryAttributeFiltersOptions } from './models';

import { getCategoryBrandsService } from '@/client/services/core/brand/methods';
import { getCategoryFiltersService } from '@/client/services/core/category/methods';
import { CategoryBrandType } from '@/server/services/core/brand/models';
import { CategoryFilterAttribute } from '@/server/services/core/category/models';

const sortSchema = (filters: CategoryFilterAttribute[]) =>
    [...filters].sort((left, right) => left.displayOrder - right.displayOrder || left.name.localeCompare(right.name, 'fa'));

const areFilterParamsEqual = (left: string[], right: string[]) => {
    if (left.length !== right.length) {
        return false;
    }

    const sortedLeft = [...left].sort();
    const sortedRight = [...right].sort();
    return sortedLeft.every((value, index) => value === sortedRight[index]);
};

const parseBrandIds = (values: string[]) => Array.from(new Set(values.map(Number).filter((value) => Number.isInteger(value) && value > 0)));

const areBrandIdsEqual = (left: number[], right: number[]) => areFilterParamsEqual(left.map(String), right.map(String));

const readQueryValues = (key: string, fallback: URLSearchParams | { getAll: (name: string) => string[] }) => {
    if (typeof window !== 'undefined') {
        return new URLSearchParams(window.location.search).getAll(key);
    }

    return fallback.getAll(key);
};

const toParamsSignature = (params: string[]) => [...params].sort().join('|');

export const useCategoryAttributeFilters = ({ categoryId, syncUrl = true, withBrands = false }: UseCategoryAttributeFiltersOptions) => {
    const router = useRouter();
    const pathname = usePathname();
    const searchParams = useSearchParams();
    const searchParamsRef = useRef(searchParams);

    const [schema, setSchema] = useState<CategoryFilterAttribute[]>([]);
    const [isLoading, setIsLoading] = useState(() => Boolean(categoryId));
    const [selection, setSelection] = useState<AttributeFiltersSelection>(createEmptyAttributeFiltersSelection);
    const [draftSelection, setDraftSelection] = useState<AttributeFiltersSelection>(createEmptyAttributeFiltersSelection);
    const requestIdRef = useRef(0);
    const lastSyncedParamsRef = useRef<string>('');
    const urlBrandIds = useMemo(
        () => (withBrands && syncUrl ? parseBrandIds(searchParams.getAll(BRAND_FILTER_QUERY_KEY)) : []),
        [searchParams, syncUrl, withBrands]
    );
    const [brandIds, setBrandIds] = useState<number[]>(() =>
        withBrands && syncUrl ? parseBrandIds(readQueryValues(BRAND_FILTER_QUERY_KEY, searchParams)) : []
    );
    const [draftBrandIds, setDraftBrandIds] = useState<number[]>(() =>
        withBrands && syncUrl ? parseBrandIds(readQueryValues(BRAND_FILTER_QUERY_KEY, searchParams)) : []
    );
    const [brands, setBrands] = useState<CategoryBrandType[]>([]);
    const [brandSearch, setBrandSearch] = useState('');
    const [isBrandsLoading, setIsBrandsLoading] = useState(false);
    const [hasBrandOptions, setHasBrandOptions] = useState(false);
    const brandIdsRef = useRef<number[]>(brandIds);
    const [knownBrands, setKnownBrands] = useState<Record<number, CategoryBrandType>>({});
    const previousCategoryIdRef = useRef(categoryId);
    const lastUrlBrandKeyRef = useRef(urlBrandIds.join('|'));

    const urlFilterParams = useMemo(() => searchParams.getAll(ATTRIBUTE_FILTER_QUERY_KEY), [searchParams]);

    useEffect(() => {
        searchParamsRef.current = searchParams;
    }, [searchParams]);

    const getLatestUrlFilterParams = useCallback(
        () => (syncUrl ? readQueryValues(ATTRIBUTE_FILTER_QUERY_KEY, searchParamsRef.current) : []),
        [syncUrl]
    );

    const getLatestUrlBrandIds = useCallback(
        () => (withBrands && syncUrl ? parseBrandIds(readQueryValues(BRAND_FILTER_QUERY_KEY, searchParamsRef.current)) : []),
        [syncUrl, withBrands]
    );

    const syncSelectionToUrl = useCallback(
        (nextSelection: AttributeFiltersSelection, nextSchema: CategoryFilterAttribute[], nextBrandIds: number[] = brandIdsRef.current) => {
            if (!syncUrl) {
                return;
            }

            const currentSearch = typeof window !== 'undefined' ? window.location.search.replace(/^\?/, '') : searchParamsRef.current.toString();
            const params = new URLSearchParams(currentSearch);
            params.delete(ATTRIBUTE_FILTER_QUERY_KEY);
            params.delete(BRAND_FILTER_QUERY_KEY);
            nextBrandIds.forEach((id) => params.append(BRAND_FILTER_QUERY_KEY, String(id)));

            const filterParams = buildAttributeFilterParams({ schema: nextSchema, selection: nextSelection });
            filterParams.forEach((param) => params.append(ATTRIBUTE_FILTER_QUERY_KEY, param));

            const query = params.toString();
            const normalizedPath = pathname.endsWith('/') ? pathname : `${pathname}/`;
            const nextUrl = query ? `${normalizedPath}?${query}` : normalizedPath;
            const currentPath = typeof window !== 'undefined' ? window.location.pathname : pathname;
            const currentQuery = currentSearch;

            lastSyncedParamsRef.current = toParamsSignature(filterParams);

            if ((currentPath === normalizedPath || currentPath === pathname) && currentQuery === query) {
                return;
            }

            router.replace(nextUrl, { scroll: false });
        },
        [pathname, router, syncUrl]
    );

    useEffect(() => {
        const categoryChanged = previousCategoryIdRef.current !== categoryId;

        if (categoryChanged) {
            brandIdsRef.current = [];
            setBrandIds([]);
            setDraftBrandIds([]);
            setBrandSearch('');
            setHasBrandOptions(false);
            lastSyncedParamsRef.current = '';
        }
        previousCategoryIdRef.current = categoryId;

        if (!categoryId) {
            setSchema([]);
            setSelection(createEmptyAttributeFiltersSelection());
            setDraftSelection(createEmptyAttributeFiltersSelection());
            setIsLoading(false);

            const latestFilters = getLatestUrlFilterParams();
            const latestBrands = getLatestUrlBrandIds();
            if (syncUrl && (latestFilters.length || latestBrands.length)) {
                syncSelectionToUrl(createEmptyAttributeFiltersSelection(), [], []);
            }

            return;
        }

        const requestId = ++requestIdRef.current;
        setIsLoading(true);

        const fetchFilters = async () => {
            try {
                const response = await getCategoryFiltersService(categoryId);
                if (requestId !== requestIdRef.current) {
                    return;
                }

                const nextSchema = sortSchema(response.data.content ?? []);
                setSchema(nextSchema);

                const latestUrlFilterParams = getLatestUrlFilterParams();
                const latestUrlBrandIds = categoryChanged ? [] : getLatestUrlBrandIds();
                const parsedFromUrl = pruneAttributeFiltersSelection(parseAttributeFilterParams(latestUrlFilterParams, nextSchema), nextSchema);

                setSelection(parsedFromUrl);
                setDraftSelection(parsedFromUrl);

                if (!categoryChanged && latestUrlBrandIds.length) {
                    brandIdsRef.current = latestUrlBrandIds;
                    setBrandIds(latestUrlBrandIds);
                    setDraftBrandIds(latestUrlBrandIds);
                    lastUrlBrandKeyRef.current = latestUrlBrandIds.join('|');
                }

                const nextParams = buildAttributeFilterParams({ schema: nextSchema, selection: parsedFromUrl });
                lastSyncedParamsRef.current = toParamsSignature(nextParams);

                if (syncUrl) {
                    const shouldRewriteUrl =
                        !areFilterParamsEqual(nextParams, latestUrlFilterParams) || !areBrandIdsEqual(brandIdsRef.current, latestUrlBrandIds);

                    if (shouldRewriteUrl) {
                        syncSelectionToUrl(parsedFromUrl, nextSchema, brandIdsRef.current);
                    }
                }
            } catch {
                if (requestId !== requestIdRef.current) {
                    return;
                }

                setSchema([]);
                setSelection(createEmptyAttributeFiltersSelection());
                setDraftSelection(createEmptyAttributeFiltersSelection());
                lastSyncedParamsRef.current = '';
            } finally {
                if (requestId === requestIdRef.current) {
                    setIsLoading(false);
                }
            }
        };

        void fetchFilters();
        // Only re-fetch schema when category changes; URL is read at response time via window/searchParams refs.
    }, [categoryId]);

    useEffect(() => {
        if (!syncUrl || !schema.length) {
            return;
        }

        const latestUrlFilterParams = getLatestUrlFilterParams();
        const signature = toParamsSignature(latestUrlFilterParams);
        if (signature === lastSyncedParamsRef.current) {
            return;
        }

        const parsed = pruneAttributeFiltersSelection(parseAttributeFilterParams(latestUrlFilterParams, schema), schema);
        const nextParams = buildAttributeFilterParams({ schema, selection: parsed });
        setSelection(parsed);
        setDraftSelection(parsed);
        lastSyncedParamsRef.current = toParamsSignature(nextParams);
    }, [getLatestUrlFilterParams, schema, syncUrl, urlFilterParams]);

    useEffect(() => {
        const urlBrandKey = urlBrandIds.join('|');
        if (urlBrandKey === lastUrlBrandKeyRef.current) {
            return;
        }

        lastUrlBrandKeyRef.current = urlBrandKey;
        if (!withBrands || !syncUrl || areBrandIdsEqual(urlBrandIds, brandIdsRef.current)) {
            return;
        }

        brandIdsRef.current = urlBrandIds;
        setBrandIds(urlBrandIds);
        setDraftBrandIds(urlBrandIds);
    }, [syncUrl, urlBrandIds, withBrands]);

    useEffect(() => {
        if (!withBrands || !categoryId) {
            setBrands([]);
            setHasBrandOptions(false);
            return;
        }

        let isCancelled = false;
        const trimmedSearch = brandSearch.trim();
        setIsBrandsLoading(true);

        const timeout = setTimeout(
            async () => {
                try {
                    const response = await getCategoryBrandsService({
                        categoryId,
                        includeChildren: true,
                        activeOnly: true,
                        ...(trimmedSearch ? { search: trimmedSearch } : {})
                    });

                    if (isCancelled) {
                        return;
                    }

                    const content = response.data.content ?? [];
                    setKnownBrands((current) => ({ ...current, ...Object.fromEntries(content.map((brand) => [brand.id, brand])) }));
                    setBrands(content);

                    if (!trimmedSearch) {
                        setHasBrandOptions(content.length > 0);
                    }
                } catch {
                    if (!isCancelled) {
                        setBrands([]);
                    }
                } finally {
                    if (!isCancelled) {
                        setIsBrandsLoading(false);
                    }
                }
            },
            trimmedSearch ? BRAND_SEARCH_DEBOUNCE_MS : 0
        );

        return () => {
            isCancelled = true;
            clearTimeout(timeout);
        };
    }, [brandSearch, categoryId, withBrands]);

    const applyBrandIds = useCallback((nextBrandIds: number[]) => {
        brandIdsRef.current = nextBrandIds;
        setBrandIds(nextBrandIds);
        setDraftBrandIds(nextBrandIds);
    }, []);

    const applySelection = useCallback(
        (nextSelection: AttributeFiltersSelection, nextBrandIds: number[] = brandIdsRef.current) => {
            const pruned = pruneAttributeFiltersSelection(nextSelection, schema);
            setSelection(pruned);
            setDraftSelection(pruned);
            applyBrandIds(nextBrandIds);
            syncSelectionToUrl(pruned, schema, nextBrandIds);
        },
        [applyBrandIds, schema, syncSelectionToUrl]
    );

    const setBrandSelection = useCallback(
        (nextBrandIds: number[], options?: { draft?: boolean }) => {
            if (options?.draft) {
                setDraftBrandIds(nextBrandIds);
                return;
            }

            applySelection(selection, nextBrandIds);
        },
        [applySelection, selection]
    );

    const setValues = useCallback(
        (slug: string, values: AttributeValueSelection[], options?: { draft?: boolean }) => {
            if (options?.draft) {
                setDraftSelection((current) => setAttributeValuesSelection(current, slug, values));
                return;
            }

            applySelection(setAttributeValuesSelection(selection, slug, values));
        },
        [applySelection, selection]
    );

    const setRange = useCallback(
        (slug: string, key: 'after' | 'before', date: string | undefined, options?: { draft?: boolean }) => {
            if (options?.draft) {
                setDraftSelection((current) => setAttributeRangeSelection(current, slug, key, date));
                return;
            }

            applySelection(setAttributeRangeSelection(selection, slug, key, date));
        },
        [applySelection, selection]
    );

    const clearAll = useCallback(
        (options?: { draft?: boolean }) => {
            if (options?.draft) {
                setDraftSelection(createEmptyAttributeFiltersSelection());
                setDraftBrandIds([]);
                return;
            }

            applySelection(createEmptyAttributeFiltersSelection(), []);
        },
        [applySelection]
    );

    const resetDraft = useCallback(() => {
        setDraftSelection(selection);
        setDraftBrandIds(brandIds);
    }, [brandIds, selection]);

    const applyDraft = useCallback(() => {
        if (hasInvalidAttributeRanges(draftSelection)) {
            return false;
        }

        applySelection(draftSelection, draftBrandIds);
        return true;
    }, [applySelection, draftBrandIds, draftSelection]);

    const attributeFilterParams = useMemo(() => buildAttributeFilterParams({ schema, selection }), [schema, selection]);

    const activeCount = useMemo(() => countActiveAttributeFilters(selection) + brandIds.length, [brandIds, selection]);
    const draftActiveCount = useMemo(() => countActiveAttributeFilters(draftSelection) + draftBrandIds.length, [draftBrandIds, draftSelection]);
    const selectedBrandOptions = useMemo(
        () =>
            Array.from(new Set([...brandIds, ...draftBrandIds]))
                .map((id) => knownBrands[id])
                .filter((brand): brand is CategoryBrandType => Boolean(brand)),
        [brandIds, draftBrandIds, knownBrands]
    );
    const hasBrands = withBrands && (hasBrandOptions || brandIds.length > 0 || brandSearch.trim().length > 0);
    const hasInvalidDraftRanges = useMemo(() => hasInvalidAttributeRanges(draftSelection), [draftSelection]);
    const hasFilters = schema.length > 0;

    return {
        schema,
        isLoading,
        hasFilters,
        selection,
        draftSelection,
        attributeFilterParams,
        brandIds,
        draftBrandIds,
        brandOptions: brands,
        selectedBrandOptions,
        brandSearch,
        hasBrands,
        isBrandsLoading,
        setBrandSearch,
        setBrandSelection,
        activeCount,
        draftActiveCount,
        hasInvalidDraftRanges,
        setValues,
        setRange,
        clearAll,
        resetDraft,
        applyDraft
    };
};
